import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client/react';

import {
  BusinessInquiryCreateDocument,
  BusinessInquiryCreateMutation,
  BusinessInquiryCreateMutationVariables,
  Partnerships__BusinessInquiryCreateInput,
  Status,
} from '@graphql/platform';

import { PartnershipLeadFormData } from '@root/constants/partnerships';

export interface CreatePartnershipLeadResult {
  success: boolean;
  error?: string;
}

/**
 * Hook for creating partnership business inquiry leads.
 *
 * This hook provides a mutation function to submit partnership lead form data
 * to the GraphQL API using the businessInquiryCreate mutation.
 *
 * @returns A function that takes PartnershipLeadFormData and returns a Promise
 *          with the result of the mutation (success/error status)
 */
export const useCreatePartnershipLead = () => {
  const client = useApolloClient();

  const createPartnershipLead = useCallback(
    async (
      formData: PartnershipLeadFormData,
    ): Promise<CreatePartnershipLeadResult> => {
      try {
        // Transform form data to match GraphQL input type
        const input: Partnerships__BusinessInquiryCreateInput = {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          organization: formData.organization,
          industry: formData.industry,
          location: formData.location,
          message: formData.message || null,
        };

        const { data } = await client.mutate<
          BusinessInquiryCreateMutation,
          BusinessInquiryCreateMutationVariables
        >({
          mutation: BusinessInquiryCreateDocument,
          variables: { input },
        });

        // Handle mutation result
        const result = data?.businessInquiryCreate;
        if (!result) {
          return {
            success: false,
            error: 'No response from server.',
          };
        }

        // Handle Status enum cases
        switch (result.status) {
          case Status.Ok:
            return {
              success: true,
            };

          case Status.NotFound:
            return {
              success: false,
              error: result.error || 'Resource not found. Please try again.',
            };

          case Status.Unauthorized:
            return {
              success: false,
              error:
                result.error ||
                'You are not authorized to perform this action.',
            };

          case Status.Unprocessable:
            return {
              success: false,
              error:
                result.error ||
                'Invalid form data. Please check your inputs and try again.',
            };

          default:
            // This should never happen if all Status enum values are handled,
            // but provides a fallback for unexpected status values
            return {
              success: false,
              error:
                result.error ||
                'An unexpected error occurred. Please try again.',
            };
        }
      } catch (error) {
        // Handle network or other errors
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred.',
        };
      }
    },
    [client],
  );

  return createPartnershipLead;
};
